"use client"

import type { ProductTypeDetails, GameDetails, GameLocalized, BookDetails, BookLocalized, BoxDetails, BoxComponent } from "@/types/product-details"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useTranslations } from "next-intl"
import { Users, Clock, Star, Building2, Calendar, Palette, BookOpen, Hash, Layers, Shield, Globe, ExternalLink, Info } from "lucide-react"

interface ProductTypeInfoProps {
  typeDetails: ProductTypeDetails
}

export default function ProductTypeInfo({ typeDetails }: ProductTypeInfoProps) {
  if (typeDetails.product_type === "game") {
    return <GameInfo details={typeDetails.details} localized={typeDetails.localized} />
  }

  if (typeDetails.product_type === "book") {
    return <BookInfo details={typeDetails.details} localized={typeDetails.localized} />
  }

  if (typeDetails.product_type === "box") {
    return <BoxInfo details={typeDetails.details} />
  }

  return null
}

/** Rental box: what is inside. Each item opens a popup with its picture and description
 *  (no navigation away from the box); the popup links to the item's own page. */
function BoxInfo({ details }: { details: BoxDetails }) {
  const t = useTranslations("product")
  const [open, setOpen] = useState<BoxComponent | null>(null)
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold font-serif text-foreground text-sm">{t("box.title")}</h3>
        <span className="text-xs text-muted-foreground">{t("box.rentalOnly")}</span>
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border">
        {details.components.map((c) => (
          <li key={c.product_id}>
            <button
              type="button"
              onClick={() => setOpen(c)}
              className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
            >
              {c.thumbnail ? (
                <img src={c.thumbnail} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-muted" />
              )}
              <span className="flex-1 text-sm text-foreground">{c.title}</span>
              {c.quantity > 1 && <span className="text-xs text-muted-foreground">{c.quantity} {t("box.pieces")}</span>}
              <Info className="w-4 h-4 text-muted-foreground" />
            </button>
          </li>
        ))}
      </ul>
      <LocalizedClientLink href="/return" className="text-sm text-primary underline underline-offset-2">
        {t("box.howReturn")}
      </LocalizedClientLink>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif">{open.title}</DialogTitle>
              </DialogHeader>
              {open.thumbnail && (
                <img src={open.thumbnail} alt="" className="w-full max-h-72 rounded-xl object-contain bg-muted" />
              )}
              {open.description && (
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{open.description}</p>
              )}
              {open.handle && (
                <LocalizedClientLink href={`/products/${open.handle}`} className="text-sm text-primary underline underline-offset-2">
                  {t("box.openItem")}
                </LocalizedClientLink>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function GameInfo({
  details,
  localized,
}: {
  details: GameDetails
  localized: GameLocalized | null
}) {
  const t = useTranslations("product")

  const playersDisplay =
    details.min_players && details.max_players
      ? details.min_players === details.max_players
        ? String(details.min_players)
        : `${details.min_players}–${details.max_players}`
      : details.min_players
        ? `${details.min_players}+`
        : null

  const complexityDisplay = details.complexity
    ? "●".repeat(details.complexity) + "○".repeat(5 - details.complexity)
    : null

  return (
    <div className="space-y-4">
      <h3 className="font-semibold font-serif text-foreground text-sm">
        {t("tabs.gameDetails")}
      </h3>

      {/* Key stats row */}
      <div className="grid grid-cols-3 gap-3">
        {playersDisplay && (
          <StatBadge icon={<Users className="w-4 h-4" />} label={t("gameInfo.players")} value={playersDisplay} />
        )}
        {details.play_time_minutes && (
          <StatBadge icon={<Clock className="w-4 h-4" />} label={t("gameInfo.playTime")} value={`${details.play_time_minutes} ${t("gameInfo.minutes")}`} />
        )}
        {complexityDisplay && (
          <StatBadge icon={<Star className="w-4 h-4" />} label={t("gameInfo.complexity")} value={complexityDisplay} />
        )}
        {details.age_rating && details.age_rating !== "Not Rated" && (
          <StatBadge icon={<Shield className="w-4 h-4" />} label={t("gameInfo.ageRating")} value={details.age_rating} />
        )}
      </div>

      {/* Detail grid */}
      <div className="flex flex-col gap-0.5 text-sm">
        <DetailRow label={t("gameInfo.publisher")} value={details.publisher} />
        <DetailRow label={t("gameInfo.year")} value={details.publication_year?.toString()} />
        <DetailRow label={t("gameInfo.designer")} value={localized?.designer_names} />
        <DetailRow label={t("gameInfo.theme")} value={localized?.theme} />
        <DetailRow label={t("gameInfo.gameType")} value={localized?.game_type} />
        <DetailRow label={t("gameInfo.category")} value={localized?.game_category_display} />
        {details.content_language && (
          <DetailRow label={t("gameInfo.language")} value={getLocalizedLangName(details.content_language, t)} />
        )}
        {details.language_independent && (
          <DetailRow label={t("gameInfo.languageIndependentLabel")} value={t("gameInfo.languageIndependentValue")} />
        )}
      </div>

      {/* Mechanics chips — prefer translated names from localized, fallback to base array */}
      {(() => {
        const mechanicsList = localized?.game_mechanics
          ? localized.game_mechanics.split(",").map(m => m.trim()).filter(Boolean)
          : details.game_mechanics
        return mechanicsList && mechanicsList.length > 0 ? (
          <div>
            <span className="text-xs text-muted-foreground">{t("gameInfo.mechanics")}</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {mechanicsList.map((mechanic) => (
                <span
                  key={mechanic}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground"
                >
                  {mechanic}
                </span>
              ))}
            </div>
          </div>
        ) : null
      })()}

      {/* How to play */}
      {localized?.how_to_play && (
        <div>
          <span className="text-xs text-muted-foreground">{t("tabs.howToPlay")}</span>
          <p className="mt-1 text-sm text-foreground-secondary leading-relaxed">{localized.how_to_play}</p>
        </div>
      )}

      {/* Rules link */}
      {details.rules_url && (
        <a
          href={details.rules_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-hover transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t("tabs.fullRules")}
        </a>
      )}

      {/* Tags */}
      {localized?.tags && localized.tags.length > 0 && (
        <div>
          <span className="text-xs text-muted-foreground">{t("gameInfo.tags")}</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {localized.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

function BookInfo({
  details,
  localized,
}: {
  details: BookDetails
  localized: BookLocalized | null
}) {
  const t = useTranslations("product")

  return (
    <div className="space-y-4">
      <h3 className="font-semibold font-serif text-foreground text-sm">
        {t("tabs.bookDetails")}
      </h3>

      {/* Key stats row */}
      <div className="grid grid-cols-3 gap-3">
        {localized?.author_names && (
          <StatBadge icon={<BookOpen className="w-4 h-4" />} label={t("bookInfo.author")} value={localized.author_names} />
        )}
        {details.page_count && (
          <StatBadge icon={<Layers className="w-4 h-4" />} label={t("bookInfo.pages")} value={details.page_count.toString()} />
        )}
        {details.isbn && !details.isbn.startsWith("TEMP") && (
          <StatBadge icon={<Hash className="w-4 h-4" />} label={t("bookInfo.isbn")} value={details.isbn} />
        )}
      </div>

      {/* Detail grid */}
      <div className="flex flex-col gap-0.5 text-sm">
        <DetailRow label={t("bookInfo.binding")} value={details.binding_type} />
        <DetailRow label={t("bookInfo.ageRecommendation")} value={details.age_recommendation} />
        <DetailRow label={t("bookInfo.genre")} value={localized?.genre_display} />
        <DetailRow label={t("bookInfo.theme")} value={localized?.theme} />
        <DetailRow label={t("bookInfo.publisher")} value={details.publisher} />
        <DetailRow label={t("bookInfo.year")} value={details.publication_year?.toString()} />
        {details.content_language && (
          <DetailRow label={t("bookInfo.language")} value={getLocalizedLangName(details.content_language, t)} />
        )}
        {details.language_independent && (
          <DetailRow label={t("bookInfo.languageIndependentLabel")} value={t("bookInfo.languageIndependentValue")} />
        )}
      </div>

      {localized?.annotation && (
        <div>
          <span className="text-xs text-muted-foreground">{t("bookInfo.annotation")}</span>
          <p className="mt-1 text-sm text-foreground-secondary leading-relaxed">{localized.annotation}</p>
        </div>
      )}

      {/* Tags */}
      {localized?.tags && localized.tags.length > 0 && (
        <div>
          <span className="text-xs text-muted-foreground">{t("bookInfo.tags")}</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {localized.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

const LANG_KEY_MAP: Record<string, string> = { en: "langEn", ru: "langRu", cs: "langCs", uk: "langUk" }

function getLocalizedLangName(langCode: string, t: (key: string) => string): string {
  const key = LANG_KEY_MAP[langCode]
  return key ? t(`filters.${key}`) : langCode
}

function StatBadge({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center text-center p-2 rounded-lg bg-background">
      <div className="text-primary mb-1">{icon}</div>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-xs font-semibold mt-0.5">{value}</span>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value === "null") return null
  return (
    <div className="py-1 border-b border-border/50">
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className="font-medium text-foreground text-sm mt-0.5">{value}</p>
    </div>
  )
}
